import type { Vertex } from '@baduk/engine';
import type { ChainOutline } from './components/Board.js';

/**
 * Just outside the stones (radius 0.46) so the border hugs them without biting,
 * and near enough half a cell that two edges from neighbouring stones in the
 * same group meet rather than leaving a dashed-looking seam between them.
 */
const OUTLINE_INSET = 0.485;
const CORNER_RADIUS = 0.15;

/** Two decimals is plenty at this scale, and keeps the path strings short. */
const round = (value: number): number => Math.round(value * 100) / 100;

interface Sides {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

/**
 * The perimeter of one group, with its corners rounded.
 *
 * A corner only exists where two sides of the *same* stone are both on the
 * perimeter — anywhere else an edge simply runs on into its neighbour's, and
 * trimming there would punch holes in a straight run. So each such corner has
 * both its edges pulled back by the radius, and a quarter turn drawn between the
 * two new ends with the corner itself as the control point.
 *
 * Inside corners, where the boundary turns back on itself around a notch, are
 * left square: they are formed by edges belonging to two different stones, and
 * they read far less harshly than the outside ones do.
 */
export function outlinePath(
  outline: ChainOutline,
  cell: number,
  x: (v: Vertex) => number,
  y: (v: Vertex) => number,
): string {
  const perimeter = new Map<Vertex, Sides>();
  for (const edge of outline.edges) {
    const sides = perimeter.get(edge.vertex) ?? {
      top: false,
      right: false,
      bottom: false,
      left: false,
    };
    sides[edge.side] = true;
    perimeter.set(edge.vertex, sides);
  }

  const o = cell * OUTLINE_INSET;
  const r = cell * CORNER_RADIUS;
  let d = '';

  for (const [vertex, sides] of perimeter) {
    const cx = x(vertex);
    const cy = y(vertex);

    // Left/top/right/bottom of this stone's cell.
    const l = round(cx - o);
    const t = round(cy - o);
    const rt = round(cx + o);
    const b = round(cy + o);

    const topLeft = sides.top && sides.left;
    const topRight = sides.top && sides.right;
    const bottomRight = sides.bottom && sides.right;
    const bottomLeft = sides.bottom && sides.left;

    if (sides.top) {
      d += `M${round(l + (topLeft ? r : 0))},${t}L${round(rt - (topRight ? r : 0))},${t}`;
    }
    if (sides.right) {
      d += `M${rt},${round(t + (topRight ? r : 0))}L${rt},${round(b - (bottomRight ? r : 0))}`;
    }
    if (sides.bottom) {
      d += `M${round(rt - (bottomRight ? r : 0))},${b}L${round(l + (bottomLeft ? r : 0))},${b}`;
    }
    if (sides.left) {
      d += `M${l},${round(b - (bottomLeft ? r : 0))}L${l},${round(t + (topLeft ? r : 0))}`;
    }

    if (topLeft) d += `M${l},${round(t + r)}Q${l},${t} ${round(l + r)},${t}`;
    if (topRight) d += `M${round(rt - r)},${t}Q${rt},${t} ${rt},${round(t + r)}`;
    if (bottomRight) d += `M${rt},${round(b - r)}Q${rt},${b} ${round(rt - r)},${b}`;
    if (bottomLeft) d += `M${round(l + r)},${b}Q${l},${b} ${l},${round(b - r)}`;
  }
  return d;
}
